import React, { useEffect, useState } from "react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { getQueueCount, processQueue } from "@/lib/offlineQueue";
import { WifiHigh, WifiSlash, CloudArrowUp, Queue } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

export default function NetworkStatus() {
  const isOnline = useOnlineStatus();
  const [queueCount, setQueueCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  const refreshCount = async () => {
    const count = await getQueueCount();
    setQueueCount(count);
  };

  useEffect(() => {
    refreshCount();
    const interval = setInterval(refreshCount, 5000);
    return () => clearInterval(interval);
  }, []);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && queueCount > 0) {
      setShowBanner(true);
    }
  }, [isOnline, queueCount]);

  // Listen for SW sync message
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === "SYNC_QUEUE") {
        handleSync();
      }
    };
    navigator.serviceWorker?.addEventListener("message", handler);
    return () => navigator.serviceWorker?.removeEventListener("message", handler);
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const results = await processQueue(api);
      await refreshCount();
      if (results.success > 0) {
        toast.success(`${results.success} data berhasil disinkronkan`);
      }
      if (results.failed > 0) {
        toast.error(`${results.failed} data gagal disinkronkan`);
      }
      if (results.success > 0 && results.failed === 0) {
        setShowBanner(false);
      }
    } catch {
      toast.error("Gagal sinkronisasi");
    }
    setSyncing(false);
  };

  return (
    <>
      {/* Offline indicator */}
      {!isOnline && (
        <div
          data-testid="offline-banner"
          className="bg-amber-500 text-white px-3 py-1.5 text-xs font-medium flex items-center justify-center gap-2"
        >
          <WifiSlash size={14} weight="bold" />
          <span>Offline — Data akan disimpan lokal</span>
          {queueCount > 0 && (
            <span className="bg-white/20 px-1.5 py-0.5 rounded text-[10px] font-bold">
              {queueCount} antrian
            </span>
          )}
        </div>
      )}

      {/* Sync banner when back online with queued data */}
      {isOnline && showBanner && queueCount > 0 && (
        <div
          data-testid="sync-banner"
          className="bg-[#B8860B] text-white px-3 py-1.5 text-xs font-medium flex items-center justify-center gap-2"
        >
          <Queue size={14} weight="bold" />
          <span>{queueCount} data menunggu sinkronisasi</span>
          <Button
            data-testid="sync-now-btn"
            size="sm"
            variant="secondary"
            onClick={handleSync}
            disabled={syncing}
            className="h-6 text-[10px] px-2 bg-white/20 text-white hover:bg-white/30 border-0"
          >
            {syncing ? (
              <CloudArrowUp size={12} className="mr-1 animate-bounce" />
            ) : (
              <CloudArrowUp size={12} className="mr-1" />
            )}
            {syncing ? "Sinkron..." : "Sinkron Sekarang"}
          </Button>
        </div>
      )}

      {/* Subtle online status indicator in sidebar area */}
      {queueCount > 0 && isOnline && !showBanner && (
        <button
          onClick={() => setShowBanner(true)}
          data-testid="queue-indicator"
          className="fixed bottom-20 right-4 z-40 bg-[#B8860B] text-white rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 shadow-lg hover:bg-[#9A7209] transition-colors"
        >
          <Queue size={14} />
          {queueCount}
        </button>
      )}
    </>
  );
}
